/**
 * Recent Generations panel — chunk-2 of the AI gen hardening.
 *
 * Durable surface in the figure-gallery left rail showing the last
 * N AI-generation jobs for this matter. Status icon + age label
 * per row; "open" button on rows whose candidates are still on disk.
 *
 * Status mapping:
 *   running / queued                            → ◉ running
 *   completed + has_candidates_on_disk          → ⚪ candidates ready
 *   completed + committed_figure_id             → ⚫ committed to FIG-N
 *   completed + !has_disk + !committed          → ⊗ candidates expired
 *   failed                                      → ⚠ failed
 *   cancelled                                   → ⊗ cancelled
 *
 * The auto-open-on-page-reload behaviour from chunk-1 was retired
 * in favour of this panel — drafter chooses when to re-open the
 * candidate modal by clicking ``open`` on a ready row.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronDown, ChevronRight, RefreshCw,
  CircleDot, CheckCircle2, AlertTriangle, Ban, Loader2,
} from 'lucide-react';
import {
  getGenerationHistory,
  getGenerateAiJob,
  type GenerationHistoryEntry,
  type DrawingFigureSummary,
} from '@/api/client';
import { useDrawingStore } from '@/store/drawingStore';

interface Props {
  matterId: string;
  /** Loaded figure list — used to label committed rows with the
   *  drafter-visible FIG. N instead of the opaque drawing_figure_id. */
  figures: DrawingFigureSummary[];
}

type RowStatus =
  | 'running' | 'ready' | 'partial' | 'all_consumed'
  | 'expired' | 'failed' | 'cancelled';


function _availableCount(entry: GenerationHistoryEntry): number {
  if (typeof entry.available_count === 'number') return entry.available_count;
  // Pre-A2 history responses shipped without consumed_count /
  // available_count; fall back to "all available if not committed".
  return entry.committed_figure_id ? 0 : entry.candidate_count;
}


function _consumedCount(entry: GenerationHistoryEntry): number {
  if (typeof entry.consumed_count === 'number') return entry.consumed_count;
  return entry.committed_figure_id ? 1 : 0;
}


function _classifyRow(entry: GenerationHistoryEntry): RowStatus {
  if (entry.status === 'running' || entry.status === 'queued') return 'running';
  if (entry.status === 'failed') return 'failed';
  if (entry.status === 'cancelled') return 'cancelled';
  // status === 'completed' — multi-pick states:
  const available = _availableCount(entry);
  const consumed = _consumedCount(entry);
  if (available > 0 && entry.has_candidates_on_disk) {
    // Some candidates still pickable. Distinguish "fresh" from
    // "partial pick" so the panel can show different status text.
    return consumed > 0 ? 'partial' : 'ready';
  }
  if (consumed > 0 && !entry.has_candidates_on_disk) {
    // All previously-pickable candidates are committed AND the
    // candidate dir is gone — terminal, no further action.
    return 'all_consumed';
  }
  if (consumed > 0) return 'all_consumed';  // dir still present but all picked
  return 'expired';
}


function _ageLabel(iso: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}


function _StatusIcon({ status }: { status: RowStatus }) {
  switch (status) {
    case 'running':
      return <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[#002FA7]" />;
    case 'ready':
    case 'partial':
      return <CircleDot className="h-3.5 w-3.5 shrink-0 text-emerald-600" />;
    case 'all_consumed':
      return <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-zinc-500" />;
    case 'failed':
      return <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />;
    case 'cancelled':
    case 'expired':
      return <Ban className="h-3.5 w-3.5 shrink-0 text-zinc-400" />;
  }
}


export const RecentGenerationsPanel: React.FC<Props> = ({ matterId, figures }) => {
  const [expanded, setExpanded] = useState(true);
  const [entries, setEntries] = useState<GenerationHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setGenerationJob = useDrawingStore((s) => s.setGenerationJob);
  const openGenerationModal = useDrawingStore((s) => s.openGenerationModal);
  // Re-fetch when the active job's status changes — covers the
  // running→completed in-page transition without polling our own
  // endpoint. The polling reducer already updates this status in
  // real time.
  const activeJobStatus = useDrawingStore((s) => s.generationJobStatus);

  const refresh = useCallback(async () => {
    if (!matterId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getGenerationHistory(matterId, 10);
      setEntries(res.jobs || []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail
        || (e as { message?: string })?.message
        || 'Failed to load generation history.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    void refresh();
  }, [refresh, activeJobStatus]);

  const onOpen = useCallback(async (entry: GenerationHistoryEntry) => {
    // Re-fetch the canonical job state (full candidate metadata)
    // before seeding the store — the panel rows only carry the
    // summary fields. The modal's progress/candidate selector
    // reads from generationJobState, which expects the polling
    // shape.
    try {
      const job = await getGenerateAiJob(matterId, entry.id);
      setGenerationJob(matterId, entry.id, job);
      openGenerationModal();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail
        || (e as { message?: string })?.message
        || 'Failed to open this generation.';
      setError(msg);
    }
  }, [matterId, setGenerationJob, openGenerationModal]);

  const figLabelById = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const f of figures) m[f.id] = f.fig_label || f.id;
    return m;
  }, [figures]);

  if (entries.length === 0 && !loading && !error) {
    // Hide the section entirely when there are no jobs — avoids
    // adding a permanent empty card to the rail. The "Generate
    // with AI" button below is the entry point.
    return null;
  }

  return (
    <section
      className="border-t border-zinc-200"
      data-testid="recent-generations-panel"
    >
      <header className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-700"
          data-testid="recent-generations-toggle"
          aria-expanded={expanded}
        >
          {expanded
            ? <ChevronDown size={12} />
            : <ChevronRight size={12} />}
          Recent Generations
          <span className="text-zinc-400">({entries.length})</span>
        </button>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={loading}
          title="Refresh list"
          className="text-zinc-400 hover:text-zinc-700 disabled:opacity-50"
          data-testid="recent-generations-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
        </button>
      </header>
      {expanded && (
        <ul className="space-y-1 px-2 pb-2" role="list">
          {error && (
            <li className="px-2 py-1.5 text-[11px] text-red-600">
              {error}
            </li>
          )}
          {entries.map((entry) => {
            const rowStatus = _classifyRow(entry);
            const age = _ageLabel(entry.created_at);
            const available = _availableCount(entry);
            const consumed = _consumedCount(entry);
            const total = entry.candidate_count;
            const committedIds =
              entry.committed_figure_ids
              || (entry.committed_figure_id ? [entry.committed_figure_id] : []);
            const usedFigLabels = committedIds
              .map((id) => figLabelById[id])
              .filter((s): s is string => Boolean(s));
            const statusText =
              rowStatus === 'running' ? 'running'
              : rowStatus === 'ready' ? `${total} candidate${total === 1 ? '' : 's'} ready`
              : rowStatus === 'partial' ? `${available} of ${total} candidates available`
              : rowStatus === 'all_consumed' ? `${consumed} of ${total} consumed${committedIds.length ? '' : ''}`
              : rowStatus === 'expired' ? 'candidates expired'
              : rowStatus === 'failed' ? 'failed'
              : 'cancelled';
            const canOpen =
              rowStatus === 'ready'
              || rowStatus === 'partial'
              || rowStatus === 'running'
              || rowStatus === 'all_consumed';  // open shows the consumed-marked view
            return (
              <li
                key={entry.id}
                data-testid={`recent-generation-row-${entry.id}`}
                data-status={rowStatus}
                className="flex items-center gap-2 rounded-sm border border-zinc-200 bg-white px-2 py-1.5 text-xs"
              >
                <_StatusIcon status={rowStatus} />
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-700 truncate" title={statusText}>
                    {statusText}
                  </div>
                  <div className="text-[10px] text-zinc-400">
                    {age}
                    {usedFigLabels.length > 0 && (
                      <>
                        {' · Used in '}
                        {usedFigLabels.join(', ')}
                      </>
                    )}
                  </div>
                </div>
                {canOpen && (
                  <button
                    type="button"
                    onClick={() => void onOpen(entry)}
                    data-testid={`recent-generation-open-${entry.id}`}
                    className="rounded-sm border border-zinc-300 bg-white px-2 py-1 text-[11px] font-medium text-[#002FA7] hover:bg-zinc-50"
                  >
                    {rowStatus === 'running' ? 'view' : 'open'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
